import {
  isPrivilegedRole,
  type PortfolioCreateInput,
  portfolioCreateInputSchema,
} from "@adpilot/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchAmazonAccounts } from "@/lib/amazon-api";
import { getApiErrorMessage } from "@/lib/api-error";
import { createPortfolio } from "@/lib/portfolio-api";
import { useAuthStore } from "@/stores/auth";

type BudgetPolicy = "noCap" | "dateRange" | "monthlyRecurring";

interface CreatePortfolioDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FieldErrors {
  shop?: string;
  name?: string;
  amount?: string;
  startDate?: string;
  endDate?: string;
}

function RequiredMark() {
  return (
    <span aria-hidden="true" className="text-destructive-foreground">
      *
    </span>
  );
}

export function CreatePortfolioDialog({ open, onOpenChange }: CreatePortfolioDialogProps) {
  const { t } = useTranslation("portfolios");
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const currentShopId = useAuthStore((state) => state.currentShopId);
  const workspaceId = user?.activeWorkspaceId ?? null;

  const accounts = useQuery({
    queryKey: ["amazon", "accounts", workspaceId],
    queryFn: fetchAmazonAccounts,
    enabled: open && Boolean(workspaceId),
  });
  // Same writability rule as the campaign-creation wizard: active + connected
  // shops of the active workspace where the user can perform Amazon writes.
  const writableShops = useMemo(() => {
    const accountShops = accounts.data?.connections.flatMap((connection) => connection.shops) ?? [];
    return accountShops.filter(
      (shop) =>
        shop.workspaceId === workspaceId &&
        shop.isActive &&
        shop.status === "connected" &&
        isPrivilegedRole(shop.role),
    );
  }, [accounts.data, workspaceId]);

  const [shopId, setShopId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [policy, setPolicy] = useState<BudgetPolicy>("noCap");
  const [amount, setAmount] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Preselect the ambient shop when it is writable; never a shop the user
  // could not create in.
  const effectiveShopId =
    shopId ?? (writableShops.some((shop) => shop.shopId === currentShopId) ? currentShopId : null);

  const mutation = useMutation({
    mutationFn: ({ shop, input }: { shop: string; input: PortfolioCreateInput }) =>
      createPortfolio(shop, input),
  });

  function reset() {
    setShopId(null);
    setName("");
    setPolicy("noCap");
    setAmount("");
    setStartDate("");
    setEndDate("");
    setErrors({});
    setSubmitError(null);
    mutation.reset();
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  function buildInput(): { shop: string; input: PortfolioCreateInput } | null {
    const nextErrors: FieldErrors = {};
    if (!effectiveShopId) nextErrors.shop = t("create.errors.shopRequired");
    if (!name.trim()) nextErrors.name = t("create.errors.nameRequired");

    const parsedAmount = Number(amount);
    if (policy !== "noCap" && (!amount || Number.isNaN(parsedAmount) || parsedAmount <= 0)) {
      nextErrors.amount = t("create.errors.amountInvalid");
    }
    if (policy === "dateRange") {
      if (!startDate) nextErrors.startDate = t("create.errors.startDateRequired");
      if (endDate && startDate && endDate < startDate) {
        nextErrors.endDate = t("create.errors.endBeforeStart");
      }
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || !effectiveShopId) return null;

    const budget: PortfolioCreateInput["budget"] =
      policy === "noCap"
        ? { policy }
        : policy === "dateRange"
          ? { policy, amount: parsedAmount, startDate, endDate: endDate || null }
          : { policy, amount: parsedAmount, endDate: endDate || null };
    const parsed = portfolioCreateInputSchema.safeParse({ name: name.trim(), budget });
    if (!parsed.success) {
      setSubmitError(t("create.errors.fallback"));
      return null;
    }
    return { shop: effectiveShopId, input: parsed.data };
  }

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitError(null);
    const payload = buildInput();
    if (!payload) return;
    try {
      const created = await mutation.mutateAsync(payload);
      toast.success(t("create.success", { name: created.name }));
      // Refreshes the list + analytics reads (all keyed under "portfolios").
      void queryClient.invalidateQueries({ queryKey: ["portfolios"] });
      handleOpenChange(false);
    } catch (mutationError) {
      setSubmitError(await getApiErrorMessage(mutationError));
    }
  }

  const shopItems = [
    { value: null, label: t("create.shopPlaceholder") },
    ...writableShops.map((shop) => ({
      value: shop.shopId,
      label: `${shop.shopName} · ${shop.marketplaceName}`,
    })),
  ];
  const policyItems = (["noCap", "dateRange", "monthlyRecurring"] as const).map((value) => ({
    value,
    label: t(`create.budgetType.${value}`),
  }));

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPopup>
        <DialogHeader>
          <DialogTitle>{t("create.title")}</DialogTitle>
        </DialogHeader>
        {/* display:contents keeps Panel/Footer as the popup's direct flex
            children — a block-level form gives DialogPanel's size-full
            ScrollArea a bogus height and pushes the footer outside the
            popup's rounded corners. */}
        <form onSubmit={submit} noValidate className="contents">
          <DialogPanel className="flex flex-col gap-4">
            <Field data-invalid={errors.shop != null}>
              <FieldLabel>
                <RequiredMark />
                {t("create.shopLabel")}
              </FieldLabel>
              <Select
                items={shopItems}
                value={effectiveShopId}
                onValueChange={(value) => value && setShopId(value)}
              >
                <SelectTrigger className="w-full" aria-invalid={errors.shop != null}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {shopItems.map((item) => (
                      <SelectItem key={item.value ?? "empty"} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {!accounts.isLoading && writableShops.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t("create.noWritableShops")}</p>
              ) : null}
              {errors.shop ? <FieldError>{errors.shop}</FieldError> : null}
            </Field>

            <Field data-invalid={errors.name != null}>
              <FieldLabel htmlFor="portfolio-create-name">
                <RequiredMark />
                {t("create.nameLabel")}
              </FieldLabel>
              <Input
                id="portfolio-create-name"
                name="name"
                value={name}
                maxLength={100}
                onChange={(event) => setName(event.target.value)}
                aria-invalid={errors.name != null}
              />
              {errors.name ? <FieldError>{errors.name}</FieldError> : null}
            </Field>

            <Field>
              <FieldLabel>{t("create.budgetTypeLabel")}</FieldLabel>
              <Select
                items={policyItems}
                value={policy}
                onValueChange={(value) => {
                  if (!value) return;
                  setPolicy(value);
                  // endDate is shared by dateRange and monthlyRecurring — don't
                  // let a date typed under one policy silently carry into the other.
                  setEndDate("");
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {policyItems.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </Field>

            {policy !== "noCap" ? (
              <Field data-invalid={errors.amount != null}>
                <FieldLabel htmlFor="portfolio-create-amount">
                  <RequiredMark />
                  {policy === "monthlyRecurring"
                    ? t("create.monthlyAmountLabel")
                    : t("create.amountLabel")}
                </FieldLabel>
                <Input
                  id="portfolio-create-amount"
                  name="amount"
                  type="number"
                  inputMode="decimal"
                  min={0.01}
                  step={0.01}
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  aria-invalid={errors.amount != null}
                />
                {errors.amount ? <FieldError>{errors.amount}</FieldError> : null}
              </Field>
            ) : null}

            {policy === "dateRange" ? (
              <div className="grid grid-cols-2 gap-3">
                <Field data-invalid={errors.startDate != null}>
                  <FieldLabel htmlFor="portfolio-create-start-date">
                    <RequiredMark />
                    {t("create.startDateLabel")}
                  </FieldLabel>
                  <Input
                    id="portfolio-create-start-date"
                    name="startDate"
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    aria-invalid={errors.startDate != null}
                  />
                  {errors.startDate ? <FieldError>{errors.startDate}</FieldError> : null}
                </Field>
                <Field data-invalid={errors.endDate != null}>
                  <FieldLabel htmlFor="portfolio-create-end-date">
                    {t("create.endDateOptional")}
                  </FieldLabel>
                  <Input
                    id="portfolio-create-end-date"
                    name="endDate"
                    type="date"
                    value={endDate}
                    min={startDate || undefined}
                    onChange={(event) => setEndDate(event.target.value)}
                    aria-invalid={errors.endDate != null}
                  />
                  {errors.endDate ? <FieldError>{errors.endDate}</FieldError> : null}
                </Field>
              </div>
            ) : null}

            {policy === "monthlyRecurring" ? (
              <Field>
                <FieldLabel htmlFor="portfolio-create-monthly-end-date">
                  {t("create.endDateOptional")}
                </FieldLabel>
                <Input
                  id="portfolio-create-monthly-end-date"
                  name="endDate"
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              </Field>
            ) : null}

            {submitError ? (
              <p className="text-xs text-destructive-foreground">{submitError}</p>
            ) : null}
          </DialogPanel>
          <DialogFooter>
            <DialogClose render={<Button variant="outline">{t("common:actions.cancel")}</Button>} />
            <Button type="submit" loading={mutation.isPending} disabled={mutation.isPending}>
              {t("create.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogPopup>
    </Dialog>
  );
}
